import React, { useCallback } from 'react';
import { Table, InputNumber, Input, Button, DatePicker } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { F9131 } from '../../../services/pw9213Api';

interface DetailGridProps {
  details: Partial<F9131>[];
  isEditing: boolean;
  names: Record<string, string>;
  updateDetailField: (rowIdx: number, field: keyof F9131, value: F9131[keyof F9131]) => void;
  onAdd: () => void;
  onDelete: (rowIdx: number) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
}

const dateToInput = (v: string | null | undefined): dayjs.Dayjs | null => {
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
};

const DetailGrid: React.FC<DetailGridProps> = ({
  details, isEditing, names,
  updateDetailField, onAdd, onDelete, resolveSingleName,
}) => {
  const handleLeaveTypeBlur = useCallback((idx: number, code: string) => {
    resolveSingleName(`leaveType_${idx}`, 'leaveType', code);
  }, [resolveSingleName]);

  const columns: ColumnsType<Partial<F9131>> = [
    {
      title: '項次',
      key: 'seq',
      width: 55,
      render: (_val, _rec, idx) => idx + 1,
    },
    {
      title: '假別代碼',
      dataIndex: 'f9131_03',
      key: 'leaveType',
      width: 90,
      render: (val: string, _rec, idx) =>
        isEditing ? (
          <Input
            value={val || ''}
            maxLength={2}
            size="small"
            style={{ width: '100%' }}
            onChange={e => updateDetailField(idx, 'f9131_03', e.target.value)}
            onBlur={e => handleLeaveTypeBlur(idx, e.target.value)}
          />
        ) : val || '',
    },
    {
      title: '假別名稱',
      key: 'leaveTypeName',
      width: 100,
      render: (_val, _rec, idx) => names[`leaveType_${idx}`] || '',
    },
    {
      title: '可用天數',
      dataIndex: 'f9131_11',
      key: 'available',
      width: 90,
      render: (val: number, _rec, idx) =>
        isEditing ? (
          <InputNumber
            value={val ?? 0}
            min={0}
            precision={2}
            size="small"
            style={{ width: '100%' }}
            onChange={v => updateDetailField(idx, 'f9131_11', v ?? 0)}
          />
        ) : (val ?? 0),
    },
    {
      title: '可用天數(備)',
      dataIndex: 'f9131_111',
      key: 'available2',
      width: 100,
      render: (val: number, _rec, idx) =>
        isEditing ? (
          <InputNumber
            value={val ?? 0}
            min={0}
            precision={2}
            size="small"
            style={{ width: '100%' }}
            onChange={v => updateDetailField(idx, 'f9131_111', v ?? 0)}
          />
        ) : (val ?? 0),
    },
    {
      title: '可用開始日期',
      dataIndex: 'f9131_12',
      key: 'startDate',
      width: 130,
      render: (val: string | null, _rec, idx) =>
        isEditing ? (
          <DatePicker
            value={dateToInput(val)}
            size="small"
            style={{ width: '100%' }}
            onChange={(_d, ds) => {
              const dateStr = typeof ds === 'string' ? ds : ds[0];
              updateDetailField(idx, 'f9131_12', dateStr ? `${dateStr}T00:00:00Z` : null);
            }}
          />
        ) : val ? dayjs(val).format('YYYY-MM-DD') : '',
    },
    {
      title: '累計請假數',
      dataIndex: 'f9131_13',
      key: 'cumLeave',
      width: 90,
      render: (val: number, _rec, idx) =>
        isEditing ? (
          <InputNumber
            value={val ?? 0}
            min={0}
            precision={2}
            size="small"
            style={{ width: '100%' }}
            onChange={v => updateDetailField(idx, 'f9131_13', v ?? 0)}
          />
        ) : (val ?? 0),
    },
    {
      title: '已用(核准數)',
      dataIndex: 'f9131_14',
      key: 'usedApproved',
      width: 100,
      render: (val: number, _rec, idx) =>
        isEditing ? (
          <InputNumber
            value={val ?? 0}
            min={0}
            precision={2}
            size="small"
            style={{ width: '100%' }}
            onChange={v => updateDetailField(idx, 'f9131_14', v ?? 0)}
          />
        ) : (val ?? 0),
    },
    ...(isEditing
      ? [
          {
            title: '操作',
            key: 'action',
            width: 60,
            render: (_val: unknown, _rec: Partial<F9131>, idx: number) => (
              <Button
                size="small"
                danger
                type="link"
                onClick={() => onDelete(idx)}
              >
                刪除
              </Button>
            ),
          } as ColumnsType<Partial<F9131>>[number],
        ]
      : []),
  ];

  return (
    <div className="pw9213-detail-grid">
      {isEditing && (
        <div className="pw9213-detail-toolbar">
          <Button size="small" type="dashed" onClick={onAdd}>
            + 明細新增
          </Button>
        </div>
      )}
      <Table<Partial<F9131>>
        dataSource={details}
        columns={columns}
        rowKey={(_rec, idx) => idx ?? 0}
        size="small"
        pagination={false}
        scroll={{ y: 300 }}
      />
    </div>
  );
};

export default React.memo(DetailGrid);
